import { useEffect, useMemo, useState } from "react";

const isProd = process.env.NODE_ENV === "production";
const API_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  (isProd ? "/api" : "http://localhost:3001");

// Minimal helper to GET/PUT/POST JSON against API_BASE
async function http(method, url, body) {
  const fullUrl = url.startsWith("/") ? `${API_BASE}${url}` : url;
  const res = await fetch(fullUrl, {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const j = await res.json().catch(() => ({}));
      const msg = j.error || j.message || JSON.stringify(j);
      throw new Error(`${res.status} ${res.statusText}${msg ? ` – ${msg}` : ""}`);
    } else {
      const text = await res.text().catch(() => "");
      throw new Error(`${res.status} ${res.statusText}${text ? ` – ${text}` : ""}`);
    }
  }
  return contentType.includes("application/json") ? res.json() : res.text();
}

const numberOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await http("GET", `/admin/pricing?t=${Date.now()}`);
        console.debug("/admin/pricing payload:", data);
        console.debug("API_BASE:", API_BASE);
        if (!mounted) return;
        // Backfill defaults if fields are missing
        const withDefaults = {
          perMileRate: 0,
          timeSensitivePct: 0,
          expeditedPct: 0,
          specialtyMultiplier: (typeof data?.specialtyMultiplier === "number" && Number.isFinite(data.specialtyMultiplier)) ? data.specialtyMultiplier : 1.5,
          preloadSurchargeUnder: 0,
          preloadSurchargeOver: 0,
          preloadThresholdFeet: 0,
          preloadThresholdPounds: 0,
          tiers: [],
          routingPreference: data?.routingPreference || "TRAFFIC_AWARE_OPTIMAL",
          computeAlternativeRoutes: typeof data?.computeAlternativeRoutes === "boolean" ? data.computeAlternativeRoutes : true,
          routeSelectionStrategy: data?.routeSelectionStrategy || "FASTEST_MIN_MILES",
          routeModifiers: {
            avoidTolls: Boolean(data?.routeModifiers?.avoidTolls),
            avoidHighways: Boolean(data?.routeModifiers?.avoidHighways),
            avoidFerries: Boolean(data?.routeModifiers?.avoidFerries),
            vehicleInfo: {
              emissionType: (data?.routeModifiers?.vehicleInfo?.emissionType) || "",
            }
          },
          maintenanceMode: Boolean(data?.maintenanceMode),
          maxMiles: typeof data?.maxMiles === "number" ? data.maxMiles : null,
          ...data,
        };

        // If no tiers came from the server, seed sensible defaults
        if (!withDefaults.tiers || withDefaults.tiers.length === 0) {
          withDefaults.tiers = [
            { orderIndex: 1, maxFeet: 4,  maxPounds: 1000,  baseFee: 0, active: true },
            { orderIndex: 2, maxFeet: 8,  maxPounds: 2000,  baseFee: 0, active: true },
            { orderIndex: 3, maxFeet: 12, maxPounds: 4000,  baseFee: 0, active: true },
            { orderIndex: 4, maxFeet: 16, maxPounds: 7000,  baseFee: 0, active: true },
            { orderIndex: 5, maxFeet: 26, maxPounds: 12500, baseFee: 0, active: true },
            { orderIndex: 6, maxFeet: 32, maxPounds: 16000, baseFee: 0, active: true },
            { orderIndex: 7, maxFeet: 64, maxPounds: 48000, baseFee: 0, active: true }
          ];
        }

        // Normalize tiers ordering
        withDefaults.tiers = (withDefaults.tiers || [])
          .slice()
          .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
          .map((t, i) => ({ active: true, ...t, orderIndex: t.orderIndex ?? i + 1 }));

        setCfg(withDefaults);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onChange = (field, value) => setCfg((prev) => ({ ...prev, [field]: value }));

  const addTier = () => {
    setCfg((prev) => {
      const nextIndex = (prev.tiers?.length || 0) + 1;
      const tiers = [...(prev.tiers || []), {
        orderIndex: nextIndex,
        maxFeet: null,
        maxPounds: null,
        baseFee: 0,
        active: true,
      }];
      return { ...prev, tiers };
    });
  };

  const removeTier = (idx) => {
    setCfg((prev) => {
      const tiers = prev.tiers.filter((_, i) => i !== idx)
        .map((t, i) => ({ ...t, orderIndex: i + 1 }));
      return { ...prev, tiers };
    });
  };

  const updateTier = (idx, field, value) => {
    setCfg((prev) => {
      const tiers = prev.tiers.map((t, i) =>
        i === idx ? { ...t, [field]: value } : t
      );
      return { ...prev, tiers };
    });
  };

  const moveTier = (idx, dir) => {
    setCfg((prev) => {
      const tiers = prev.tiers.slice();
      const j = idx + dir;
      if (j < 0 || j >= tiers.length) return prev;
      const tmp = tiers[idx];
      tiers[idx] = tiers[j];
      tiers[j] = tmp;
      // Re-number indices
      tiers.forEach((t, i) => (t.orderIndex = i + 1));
      return { ...prev, tiers };
    });
  };

  const validate = useMemo(() => {
    if (!cfg) return { ok: false, issues: ["No config loaded"] };
    const issues = [];
    const tiers = (cfg.tiers || []).slice().sort((a, b) => a.orderIndex - b.orderIndex);

    // Basic checks
    if (!(tiers.length > 0)) issues.push("Add at least one tier.");
    if (cfg.perMileRate < 0) issues.push("Per‑mile rate must be ≥ 0.");
    if (cfg.timeSensitivePct < 0) issues.push("Time‑sensitive % must be ≥ 0.");
    if (cfg.expeditedPct < 0) issues.push("Expedited % must be ≥ 0.");
    if (cfg.specialtyMultiplier != null && Number(cfg.specialtyMultiplier) < 1) issues.push("Specialty multiplier must be ≥ 1 (e.g., 1.5 for +50%).");

    // Ascending thresholds & required baseFee
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (t.baseFee == null || Number(t.baseFee) < 0) issues.push(`Tier ${i + 1}: base fee is required and must be ≥ 0.`);
      if (i > 0) {
        const p = tiers[i - 1];
        if (p.maxFeet != null && t.maxFeet != null && Number(t.maxFeet) < Number(p.maxFeet)) {
          issues.push(`Tier ${i + 1}: maxFeet must be ≥ previous tier.`);
        }
        if (p.maxPounds != null && t.maxPounds != null && Number(t.maxPounds) < Number(p.maxPounds)) {
          issues.push(`Tier ${i + 1}: maxPounds must be ≥ previous tier.`);
        }
      }
    }

    return { ok: issues.length === 0, issues };
  }, [cfg]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      // Coerce numbers where appropriate
      const body = {
        perMileRate: Number(cfg.perMileRate),
        timeSensitivePct: Number(cfg.timeSensitivePct),
        expeditedPct: Number(cfg.expeditedPct),
        specialtyMultiplier: Number(cfg.specialtyMultiplier),
        preloadSurchargeUnder: Number(cfg.preloadSurchargeUnder),
        preloadSurchargeOver: Number(cfg.preloadSurchargeOver),
        preloadThresholdFeet: Number(cfg.preloadThresholdFeet),
        preloadThresholdPounds: Number(cfg.preloadThresholdPounds),
        tiers: (cfg.tiers || []).map((t, i) => ({
          orderIndex: i + 1,
          maxFeet: numberOrNull(t.maxFeet),
          maxPounds: numberOrNull(t.maxPounds),
          baseFee: Number(t.baseFee),
          active: Boolean(t.active),
        })),
        routingPreference: cfg.routingPreference,
        computeAlternativeRoutes: Boolean(cfg.computeAlternativeRoutes),
        routeSelectionStrategy: cfg.routeSelectionStrategy,
        routeModifiers: {
          avoidTolls: Boolean(cfg.routeModifiers?.avoidTolls),
          avoidHighways: Boolean(cfg.routeModifiers?.avoidHighways),
          avoidFerries: Boolean(cfg.routeModifiers?.avoidFerries),
          vehicleInfo: {
            emissionType: cfg.routeModifiers?.vehicleInfo?.emissionType || undefined,
          },
        },
        maintenanceMode: Boolean(cfg.maintenanceMode),
        maxMiles: numberOrNull(cfg.maxMiles),
      };
      if (!validate.ok) throw new Error("Please fix validation issues before saving.");
      let saved;
      try {
        saved = await http("PUT", "/admin/pricing", body);
      } catch (err) {
        const msg = String(err?.message || "");
        if (msg.startsWith("404") || msg.startsWith("405") || msg.startsWith("501") || msg.includes("Cannot PUT")) {
          saved = await http("POST", "/admin/pricing", body);
        } else {
          throw err;
        }
      }
      setCfg((prev) => ({ ...prev, ...saved }));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Loading pricing…</div>;
  if (error) return (
    <div style={{ padding: 16 }}>
      <div style={{ color: "#b91c1c", fontWeight: 600, marginBottom: 8 }}>Failed to load pricing</div>
      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#fff7ed", border: "1px solid #fde68a", padding: 12, borderRadius: 6, color: "#92400e" }}>{error}</pre>
    </div>
  );
  if (!cfg) return null;

  return (
    <div className="container" style={{ maxWidth: 980 }}>
      <h2 style={{ marginTop: 0 }}>General Settings</h2>
      <section style={{ marginBottom: 12, padding: 10, border: "1px solid var(--border)", borderRadius: 6 }}>
        <label className="inline-check" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={!!cfg.maintenanceMode}
            onChange={(e) => onChange("maintenanceMode", e.target.checked)}
          />
          <span><b>Maintenance Mode</b> — when enabled, the public calculator shows a maintenance message.</span>
        </label>
      </section>

      <h2 style={{ marginTop: 16 }}>Pricing Settings</h2>

      <section className="grid-3" style={{ marginBottom: 16 }}>
        <label>Per‑mile rate ($)<br />
          <input type="number" step="0.01" value={cfg.perMileRate} onChange={(e) => onChange("perMileRate", e.target.value)} />
        </label>
        <label>Time‑sensitive uplift (%)<br />
          <input type="number" step="0.01" value={cfg.timeSensitivePct} onChange={(e) => onChange("timeSensitivePct", e.target.value)} />
        </label>
        <label>Expedited uplift (%)<br />
          <input type="number" step="0.01" value={cfg.expeditedPct} onChange={(e) => onChange("expeditedPct", e.target.value)} />
        </label>
        <label>Specialty multiplier (×)<br />
          <input
            type="number"
            step="0.01"
            min="1"
            value={cfg.specialtyMultiplier}
            onChange={(e) => onChange("specialtyMultiplier", e.target.value)}
          />
          <div style={{ fontSize: 12, color: "var(--muted)" }}>
            Example: 1.5 = +50% on top of Standard (Oversized / After‑Hours / Weekend / Holiday)
          </div>
        </label>

        <label>Preload surcharge (≤ 26ft or ≤ 12,500lbs)<br />
          <input type="number" step="1" value={cfg.preloadSurchargeUnder} onChange={(e) => onChange("preloadSurchargeUnder", e.target.value)} />
        </label>
        <label>Preload surcharge (> 26ft or > 12,500lbs)<br />
          <input type="number" step="1" value={cfg.preloadSurchargeOver} onChange={(e) => onChange("preloadSurchargeOver", e.target.value)} />
        </label>

        <label>Preload threshold (feet)<br />
          <input type="number" step="1" value={cfg.preloadThresholdFeet} onChange={(e) => onChange("preloadThresholdFeet", e.target.value)} />
        </label>
        <label>Preload threshold (lbs)<br />
          <input type="number" step="1" value={cfg.preloadThresholdPounds} onChange={(e) => onChange("preloadThresholdPounds", e.target.value)} />
        </label>
        <label>Max miles allowed (null = no limit)<br />
          <input
            type="number"
            step="1"
            value={cfg.maxMiles ?? ""}
            onChange={(e) => onChange("maxMiles", e.target.value === "" ? null : Number(e.target.value))}
          />
        </label>
      </section>

      <h3 style={{ marginTop: 24 }}>Routing Settings</h3>
      <section className="grid-3 routing-grid" style={{ marginBottom: 16 }}>
        <label>Routing Preference<br />
          <select value={cfg.routingPreference} onChange={(e) => onChange("routingPreference", e.target.value)}>
            <option value="TRAFFIC_UNAWARE">TRAFFIC_UNAWARE</option>
            <option value="TRAFFIC_AWARE">TRAFFIC_AWARE</option>
            <option value="TRAFFIC_AWARE_OPTIMAL">TRAFFIC_AWARE_OPTIMAL</option>
          </select>
        </label>
        <label className="inline-check">
          <input
            type="checkbox"
            checked={!!cfg.computeAlternativeRoutes}
            onChange={(e) => onChange("computeAlternativeRoutes", e.target.checked)}
          />
          <span>Compute Alternative Routes</span>
        </label>
        <label>Route Selection Strategy<br />
          <select value={cfg.routeSelectionStrategy} onChange={(e) => onChange("routeSelectionStrategy", e.target.value)}>
            <option value="FASTEST_MIN_MILES">Fastest route with minimum miles</option>
            <option value="OPTIMAL_SECOND_OR_LONGEST_OF_TWO">Optimal (2nd of 3; longest if 2; only if 1)</option>
            <option value="MAX_DISTANCE">Maximum distance</option>
          </select>
        </label>

        <div style={{ gridColumn: "1 / span 3", borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 8 }}>
          <b>Route Modifiers</b>
          <div className="route-mod-grid" style={{ marginTop: 8 }}>
            <label className="label-check">
              <input type="checkbox" checked={!!cfg.routeModifiers?.avoidTolls} onChange={(e) => setCfg(p => ({ ...p, routeModifiers: { ...p.routeModifiers, avoidTolls: e.target.checked }}))} />
              <span>Avoid Tolls</span>
            </label>
            <label className="label-check">
              <input type="checkbox" checked={!!cfg.routeModifiers?.avoidHighways} onChange={(e) => setCfg(p => ({ ...p, routeModifiers: { ...p.routeModifiers, avoidHighways: e.target.checked }}))} />
              <span>Avoid Highways</span>
            </label>
            <label className="label-check">
              <input type="checkbox" checked={!!cfg.routeModifiers?.avoidFerries} onChange={(e) => setCfg(p => ({ ...p, routeModifiers: { ...p.routeModifiers, avoidFerries: e.target.checked }}))} />
              <span>Avoid Ferries</span>
            </label>
            <label>Emission Type<br />
              <select value={cfg.routeModifiers?.vehicleInfo?.emissionType || ""} onChange={(e) => setCfg(p => ({ ...p, routeModifiers: { ...p, vehicleInfo: { ...(p.routeModifiers?.vehicleInfo||{}), emissionType: e.target.value }}}))}>
                <option value="">(none)</option>
                <option value="ELECTRIC">ELECTRIC</option>
                <option value="GASOLINE">GASOLINE</option>
                <option value="DIESEL">DIESEL</option>
                <option value="HYBRID">HYBRID</option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <h3 style={{ marginTop: 24 }}>Tiers</h3>
      <div className="table-wrap tiers-wrap">
        <table className="table sticky">
          <thead>
            <tr>
              <th>#</th>
              <th>maxFeet</th>
              <th>maxPounds</th>
              <th>baseFee ($)</th>
              <th>active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cfg.tiers.map((t, idx) => (
              <tr key={idx}>
                <td>{t.orderIndex}</td>
                <td>
                  <input
                    type="number"
                    placeholder="null = open"
                    value={t.maxFeet ?? ""}
                    onChange={(e) => updateTier(idx, "maxFeet", e.target.value === "" ? null : Number(e.target.value))}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    placeholder="null = open"
                    value={t.maxPounds ?? ""}
                    onChange={(e) => updateTier(idx, "maxPounds", e.target.value === "" ? null : Number(e.target.value))}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="1"
                    value={t.baseFee}
                    onChange={(e) => updateTier(idx, "baseFee", Number(e.target.value))}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={!!t.active}
                    onChange={(e) => updateTier(idx, "active", e.target.checked)}
                  />
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn-compact" onClick={() => moveTier(idx, -1)} disabled={idx === 0} style={{ marginRight: 6 }}>↑</button>
                  <button className="btn-compact" onClick={() => moveTier(idx, 1)} disabled={idx === cfg.tiers.length - 1} style={{ marginRight: 6 }}>↓</button>
                  <button className="btn-compact" onClick={() => removeTier(idx)} style={{ color: "#b91c1c" }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <button className="btn-ghost btn-sm" onClick={addTier}>Add Tier</button>
          <button className="btn-accent" onClick={save} disabled={!validate.ok || saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
        {!validate.ok && (
          <div style={{ marginTop: 8, color: "#b91c1c" }}>
            <b>Fix these issues before saving:</b>
            <ul>
              {validate.issues.map((i, k) => <li key={k}>{i}</li>)}
            </ul>
          </div>
        )}
        {validate.ok && (
          <div style={{ marginTop: 8, color: "#047857" }}>Looks good.</div>
        )}
      </div>
    </div>
  );
}